import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatUploadDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

const Leads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit] = useState(50);
  
  // Filters
  const [search, setSearch] = useState("");
  const [selectedStates, setSelectedStates] = useState("");
  const [isDuplicate, setIsDuplicate] = useState("");
  const [duplicateType, setDuplicateType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({ states: [], banks: [], sources: [], upload_dates: [] });
  
  // Modal states
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [editTags, setEditTags] = useState("");
  
  // Bulk delete states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(""); // "all" or "filtered"
  const [deleting, setDeleting] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: "uploaded_at",
        sort_order: sortOrder,
      });
      
      if (search) params.append("search", search);
      if (selectedStates) params.append("state", selectedStates);
      if (isDuplicate !== "") params.append("is_duplicate", isDuplicate);
      if (duplicateType) params.append("duplicate_type", duplicateType);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);

      const response = await fetch(`${API}/leads?${params}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, selectedStates, isDuplicate, duplicateType, dateFrom, dateTo, sortOrder]);

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch(`${API}/filters/options`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error("Failed to fetch filter options:", error);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLeads();
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedStates("");
    setIsDuplicate("");
    setDuplicateType("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setViewModalOpen(true);
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setEditNotes(lead.notes || "");
    setEditTags(lead.tags?.join(", ") || "");
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API}/leads/${selectedLead.lead_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          notes: editNotes,
          tags: editTags.split(",").map((t) => t.trim()).filter((t) => t),
        }),
      });

      if (response.ok) {
        toast.success("Lead updated successfully");
        setEditModalOpen(false);
        fetchLeads();
      } else {
        toast.error("Failed to update lead");
      }
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      const response = await fetch(`${API}/leads/${leadId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Lead deleted successfully");
        fetchLeads();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete lead");
      }
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const openDeleteModal = (mode) => {
    setDeleteMode(mode);
    setDeleteModalOpen(true);
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      const filters = {};
      if (deleteMode === "filtered") {
        if (search) filters.search = search;
        if (selectedStates) filters.state = selectedStates;
        if (isDuplicate !== "") filters.is_duplicate = isDuplicate === "true";
        if (duplicateType) filters.duplicate_type = duplicateType;
        if (dateFrom) filters.date_from = dateFrom;
        if (dateTo) filters.date_to = dateTo;
      }

      const response = await fetch(`${API}/leads/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          delete_all: deleteMode === "all",
          filters: filters,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setDeleteModalOpen(false);
        setPage(1);
        fetchLeads();
        fetchFilterOptions();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete leads");
      }
    } catch (error) {
      toast.error("Failed to delete leads");
    } finally {
      setDeleting(false);
    }
  };

  const hasActiveFilters = search || selectedStates || isDuplicate !== "" || duplicateType || dateFrom || dateTo;

  return (
    <Layout title="Leads" subtitle="Manage and search your leads">
      <Card className="border-border/60">
        <CardContent className="p-4 lg:p-6">
          {/* Search & Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="leads-search-input"
                  placeholder="Search by phone, email, name, city..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit" data-testid="leads-search-btn">
                Search
              </Button>
            </form>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button variant="outline" onClick={() => fetchLeads()} data-testid="refresh-leads-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              {/* Bulk Delete Button - Admin Only */}
              {user?.role === "Admin" && (
                <Select 
                  value="" 
                  onValueChange={(value) => openDeleteModal(value)}
                >
                  <SelectTrigger className="w-[140px] border-destructive text-destructive" data-testid="bulk-delete-trigger">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-destructive">
                      Delete ALL Leads
                    </SelectItem>
                    {hasActiveFilters && (
                      <SelectItem value="filtered" className="text-destructive">
                        Delete Filtered ({total.toLocaleString()})
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-muted/30 rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                  State
                </label>
                <Select value={selectedStates || "all"} onValueChange={(v) => setSelectedStates(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-state">
                    <SelectValue placeholder="All states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All states</SelectItem>
                    {filterOptions.states.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                  Duplicate Status
                </label>
                <Select value={isDuplicate || "all"} onValueChange={(v) => setIsDuplicate(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-duplicate">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Duplicates Only</SelectItem>
                    <SelectItem value="false">Unique Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                  Duplicate Type
                </label>
                <Select value={duplicateType || "all"} onValueChange={(v) => setDuplicateType(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-duplicate-type">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="historical">Historical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                  Uploaded From
                </label>
                <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} data-testid="filter-date-from" />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                  Uploaded To
                </label>
                <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} data-testid="filter-date-to" />
              </div>

              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="w-full" data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing <span className="font-medium text-foreground">{leads.length}</span> of{" "}
              <span className="font-medium text-foreground">{total.toLocaleString()}</span> leads
              {hasActiveFilters && <span className="text-primary ml-1">(filtered)</span>}
            </p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No leads found</p>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">
                      <button
                        type="button"
                        onClick={() => { setSortOrder(sortOrder === "desc" ? "asc" : "desc"); setPage(1); }}
                        className="inline-flex items-center gap-1 uppercase hover:text-foreground transition-colors"
                        data-testid="sort-uploaded-date-btn"
                      >
                        Upload Date
                        {sortOrder === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                      </button>
                    </th>
                    <th className="text-left py-3 px-2">Name</th>
                    <th className="text-left py-3 px-2">Phone</th>
                    <th className="text-left py-3 px-2">Email</th>
                    <th className="text-left py-3 px-2">City</th>
                    <th className="text-left py-3 px-2">State</th>
                    <th className="text-left py-3 px-2">Source</th>
                    <th className="text-center py-3 px-2">Status</th>
                    <th className="text-right py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.lead_id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-2 text-sm tabular-nums whitespace-nowrap" data-testid={`lead-uploaded-date-${lead.lead_id}`}>
                        {formatUploadDate(lead.uploaded_at)}
                      </td>
                      <td className="py-3 px-2 font-medium">
                        {lead.first_name} {lead.last_name}
                      </td>
                      <td className="py-3 px-2 tabular-nums">{lead.phone}</td>
                      <td className="py-3 px-2 text-sm truncate max-w-[200px]">{lead.email}</td>
                      <td className="py-3 px-2">{lead.city}</td>
                      <td className="py-3 px-2">{lead.state}</td>
                      <td className="py-3 px-2 text-sm">{lead.source_name}</td>
                      <td className="py-3 px-2 text-center">
                        {lead.is_duplicate ? (
                          <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/30">
                            {lead.duplicate_type}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
                            unique
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewLead(lead)}
                            data-testid={`view-lead-${lead.lead_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {user?.role !== "Viewer" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditLead(lead)}
                              data-testid={`edit-lead-${lead.lead_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {user?.role === "Admin" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteLead(lead.lead_id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-lead-${lead.lead_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Name</p>
                <p className="font-medium">{selectedLead.first_name} {selectedLead.last_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Phone</p>
                <p className="font-medium tabular-nums">{selectedLead.phone}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Email</p>
                <p className="font-medium">{selectedLead.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Address</p>
                <p className="font-medium">{selectedLead.street}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">City</p>
                <p className="font-medium">{selectedLead.city}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">State / ZIP</p>
                <p className="font-medium">{selectedLead.state} {selectedLead.zip}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Bank</p>
                <p className="font-medium">{selectedLead.bank}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Source</p>
                <p className="font-medium">{selectedLead.source_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                <p className="font-medium">
                  {selectedLead.is_duplicate ? (
                    <Badge variant="outline" className="bg-orange-500/10 text-orange-500">
                      Duplicate ({selectedLead.duplicate_type})
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-green-500/10 text-green-500">
                      Unique
                    </Badge>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Aged</p>
                <p className="font-medium">{selectedLead.aged}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Notes</p>
                <p className="font-medium">{selectedLead.notes || "No notes"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Tags</p>
                <div className="flex gap-1 flex-wrap mt-1">
                  {selectedLead.tags?.length > 0 ? (
                    selectedLead.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">{tag}</Badge>
                    ))
                  ) : (
                    <p className="text-muted-foreground">No tags</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                data-testid="edit-notes-input"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add notes about this lead..."
                rows={4}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Tags (comma separated)</label>
              <Input
                data-testid="edit-tags-input"
                value={editTags}
                onChange={(e) => setEditTags(e.target.value)}
                placeholder="e.g., hot lead, follow-up, vip"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} data-testid="save-lead-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {deleteMode === "all" ? "Delete All Leads" : "Delete Filtered Leads"}
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {deleteMode === "all" ? (
              <p className="text-sm">
                You are about to delete <strong className="text-destructive">ALL leads</strong> from the database. 
                This will permanently remove all {total.toLocaleString()} leads.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm">
                  You are about to delete <strong className="text-destructive">{total.toLocaleString()} leads</strong> matching your current filters:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  {search && <li>Search: "{search}"</li>}
                  {selectedStates && <li>State: {selectedStates}</li>}
                  {isDuplicate && <li>Duplicates: {isDuplicate === "true" ? "Yes" : "No"}</li>}
                  {duplicateType && <li>Type: {duplicateType}</li>}
                  {dateFrom && <li>Uploaded from: {formatUploadDate(dateFrom)}</li>}
                  {dateTo && <li>Uploaded to: {formatUploadDate(dateTo)}</li>}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete} 
              disabled={deleting}
              data-testid="confirm-bulk-delete"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleteMode === "all" ? "Delete All" : `Delete ${total.toLocaleString()}`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Leads;
